import React, { useEffect, useMemo, useState } from 'react';
import DreIndicadores from '../components/DreIndicadores';
import DrePlanoAcao from '../components/DrePlanoAcao';
import DreUnidadeSelect from '../components/DreUnidadeSelect';
import { nomeMes, formatValor } from '../utils/dreFormat';
import { exportXlsx } from '../utils/xlsx';
import { IconChart, IconBuilding, IconAlert, IconDoc } from '../components/Icons';
import './Dre.css';

// Período inicial = mês anterior ao atual (mês normalmente já fechado).
function periodoInicial() {
  const hoje = new Date();
  const anterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  return { ano: anterior.getFullYear(), mes: anterior.getMonth() + 1 };
}

const displayUnidade = (u) => `${u.filialNome || u.filialChave} (${u.filialChave})`;

// Visão consolidada (somente leitura) para diretoria/DECON. Planilha única com o
// período escolhido, em duas visões:
//  • Regionais (padrão): valores das regiões em si (fato/justificativa regional);
//  • Unidades: todas as unidades de todas as regiões (fato/justificativa unidade).
// Cada indicador tem colunas de Orçado, Realizado e Justificativa. Filtros de
// regional e unidade só aparecem na visão de Unidades. Exportação .xlsx (cabeçalho
// em duas linhas: indicador acima, subcolunas abaixo); cabeçalho e coluna de
// identidade fixos na rolagem; ordenação clicando nas colunas. Autorização de
// leitor global (no servidor).
function DreDiretoria() {
  const inicial = useMemo(periodoInicial, []);
  const [ano, setAno] = useState(inicial.ano);
  const [mes, setMes] = useState(inicial.mes);

  // Visão: 'regionais' (padrão) | 'unidades'.
  const [visao, setVisao] = useState('regionais');

  const [unidades, setUnidades] = useState([]);
  const [regioes, setRegioes] = useState([]);
  const [indicadoresMeta, setIndicadoresMeta] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros locais (só na visão de Unidades) aplicados sobre a lista carregada.
  const [regionalSel, setRegionalSel] = useState('');   // '' = todas
  const [unidadeSel, setUnidadeSel] = useState('');      // '' = todas (filialChave)

  // Ordenação: key = 'ident' | `${indKey}::orcado|realizado|justificativa`.
  const [sort, setSort] = useState({ key: 'ident', dir: 'asc' });

  // Detalhe de uma linha (unidade ou região), read-only.
  const [detalhe, setDetalhe] = useState(null);
  const [detalheId, setDetalheId] = useState(null);
  const [detalheNome, setDetalheNome] = useState(null);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [errorDetalhe, setErrorDetalhe] = useState(null);

  const anosDisponiveis = useMemo(() => {
    const atual = inicial.mes === 12 ? inicial.ano + 1 : inicial.ano;
    return [atual, atual - 1, atual - 2, atual - 3];
  }, [inicial]);

  const ehRegionais = visao === 'regionais';

  // Carrega os dados do período conforme a visão selecionada.
  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    setDetalheId(null);
    setDetalhe(null);
    setErrorDetalhe(null);
    const url = ehRegionais
      ? `/api/getDreTodasRegioes?ano=${ano}&mes=${mes}`
      : `/api/getDreTodasUnidades?ano=${ano}&mes=${mes}`;
    fetch(url)
      .then(res => res.json())
      .then(data => {
        if (cancelado) return;
        if (data.error) {
          setError(data.error); setUnidades([]); setRegioes([]); setIndicadoresMeta([]);
        } else {
          setError(null);
          setIndicadoresMeta(data.indicadoresMeta || []);
          if (ehRegionais) { setRegioes(data.regioes || []); }
          else { setUnidades(data.unidades || []); }
        }
      })
      .catch(() => { if (!cancelado) setError('Erro ao carregar dados do DRE'); })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, [visao, ehRegionais, ano, mes]);

  // Linhas normalizadas (identidade + indicadores), independentes da visão.
  const linhasBrutas = useMemo(() => {
    if (ehRegionais) {
      return regioes.map(r => ({
        id: r.regiao,
        nome: r.regiao,
        sub: null,
        regiao: r.regiao,
        indicadores: r.indicadores
      }));
    }
    return unidades.map(u => ({
      id: u.filialChave,
      nome: u.filialNome || u.filialChave,
      sub: `${u.filialChave} • ${u.regiao || '—'}`,
      regiao: u.regiao,
      filialChave: u.filialChave,
      indicadores: u.indicadores
    }));
  }, [ehRegionais, regioes, unidades]);

  // Regionais distintas (filtro da visão de Unidades).
  const regionais = useMemo(
    () => [...new Set(unidades.map(u => u.regiao).filter(Boolean))].sort(),
    [unidades]
  );

  // Unidades disponíveis no filtro de unidade (limitadas pela regional escolhida).
  const unidadesDoFiltro = useMemo(() => {
    const base = regionalSel ? unidades.filter(u => u.regiao === regionalSel) : unidades;
    return [...base].sort((a, b) =>
      (a.filialNome || a.filialChave).localeCompare(b.filialNome || b.filialChave, 'pt-BR')
    );
  }, [unidades, regionalSel]);

  // Linhas exibidas após aplicar filtros (só relevantes na visão de Unidades).
  const linhas = useMemo(() => {
    if (ehRegionais) return linhasBrutas;
    return linhasBrutas.filter(row =>
      (!regionalSel || row.regiao === regionalSel) &&
      (!unidadeSel || row.filialChave === unidadeSel)
    );
  }, [ehRegionais, linhasBrutas, regionalSel, unidadeSel]);

  // Valor de ordenação de uma linha para a coluna `key`.
  const sortValue = (row, key) => {
    if (key === 'ident') return row.nome;
    const [indKey, field] = key.split('::');
    const ci = row.indicadores.find(i => i.indicador === indKey);
    if (!ci) return field === 'justificativa' ? '' : null;
    if (field === 'justificativa') return ci.justificativa || '';
    return ci[field]; // número ou null
  };

  const linhasOrdenadas = useMemo(() => {
    const arr = [...linhas];
    const { key, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const va = sortValue(a, key);
      const vb = sortValue(b, key);
      const na = va === null || va === undefined || va === '';
      const nb = vb === null || vb === undefined || vb === '';
      if (na && nb) return 0;
      if (na) return 1;    // vazios sempre por último
      if (nb) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mul;
      return String(va).localeCompare(String(vb), 'pt-BR', { numeric: true }) * mul;
    });
    return arr;
  }, [linhas, sort]);

  const sortBy = (key) => {
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' });
  };
  const arrow = (key) => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '');

  const trocarVisao = (valor) => {
    if (valor === visao) return;
    setVisao(valor);
    setRegionalSel('');
    setUnidadeSel('');
    setSort({ key: 'ident', dir: 'asc' });
  };

  const trocarRegional = (valor) => {
    setRegionalSel(valor);
    setUnidadeSel('');
  };

  // Cor do realizado vs orçado: 'pos' (verde) se maior, 'neg' (vermelho) se menor.
  const corRealizado = (ci) => {
    if (ci.orcado === null || ci.orcado === undefined ||
        ci.realizado === null || ci.realizado === undefined) return '';
    if (ci.realizado > ci.orcado) return 'pos';
    if (ci.realizado < ci.orcado) return 'neg';
    return '';
  };

  const exportar = () => {
    if (linhasOrdenadas.length === 0) return;
    // Colunas de identidade variam com a visão.
    const idCols = ehRegionais ? ['Regional'] : ['Unidade', 'Centro', 'Regional'];

    // Uma coluna por subcoluna, com o nome completo do indicador (sem traço):
    // "Faturamento Bruto Orçado", "Faturamento Bruto Realizado", etc.
    const header = [...idCols];
    indicadoresMeta.forEach(ind => {
      header.push(`${ind.label} Orçado`, `${ind.label} Realizado`, `${ind.label} Justificativa`);
    });

    const dados = linhasOrdenadas.map(row => {
      const linha = ehRegionais
        ? [row.nome]
        : [row.nome, row.filialChave, row.regiao || ''];
      row.indicadores.forEach(ci => {
        linha.push(
          ci.orcado === null || ci.orcado === undefined ? '' : ci.orcado,
          ci.realizado === null || ci.realizado === undefined ? '' : ci.realizado,
          ci.justificativa || ''
        );
      });
      return linha;
    });

    const mm = String(mes).padStart(2, '0');
    const escopo = ehRegionais ? 'Regionais' : 'Unidades';
    exportXlsx(
      `DRE_Consolidado_${escopo}_${ano}-${mm}.xlsx`,
      `DRE ${mm}-${ano}`,
      [header, ...dados]
    );
  };

  const abrirDetalhe = (row) => {
    setDetalheId(row.id);
    setDetalheNome(row.nome);
    setLoadingDetalhe(true);
    setErrorDetalhe(null);
    setDetalhe(null);
    const url = ehRegionais
      ? `/api/getDreVisaoRegiao?ano=${ano}&mes=${mes}&regiao=${encodeURIComponent(row.id)}`
      : `/api/getDreVisaoUnidade?ano=${ano}&mes=${mes}&filialChave=${encodeURIComponent(row.id)}`;
    fetch(url)
      .then(res => res.json())
      .then(data => {
        if (data.error) setErrorDetalhe(data.error);
        else setDetalhe(data);
      })
      .catch(() => setErrorDetalhe('Erro ao carregar detalhe'))
      .finally(() => setLoadingDetalhe(false));
  };

  const voltarAoResumo = () => {
    setDetalheId(null);
    setDetalhe(null);
    setErrorDetalhe(null);
  };

  const identLabel = ehRegionais ? 'Regional' : 'Unidade';
  const substantivo = ehRegionais ? 'regional' : 'unidade';
  const substantivoPlural = ehRegionais ? 'regionais' : 'unidades';

  return (
    <div className="dre-container dre-container--wide">
      <div className="dre-topbar">
        <div className="dre-header">
          <h2><span className="page-icon"><IconChart /></span>DRE — Visão Consolidada</h2>
          <p>Diretoria e DECON • {ehRegionais ? 'regionais' : 'todas as unidades'} • somente leitura</p>
        </div>

        <div className="dre-toolbar">
          <label className="dre-filtro">
            <span>Visão</span>
            <select value={visao} onChange={e => trocarVisao(e.target.value)}>
              <option value="regionais">Regionais</option>
              <option value="unidades">Unidades</option>
            </select>
          </label>

          {!ehRegionais && (
            <>
              <label className="dre-filtro">
                <span>Regional</span>
                <select value={regionalSel} onChange={e => trocarRegional(e.target.value)}>
                  <option value="">Todas</option>
                  {regionais.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
              <label className="dre-filtro">
                <span>Unidade (centro)</span>
                <DreUnidadeSelect
                  options={unidadesDoFiltro.map(u => ({ value: u.filialChave, label: displayUnidade(u) }))}
                  value={unidadeSel}
                  onChange={setUnidadeSel}
                  placeholder="Todas"
                />
              </label>
            </>
          )}

          <label className="dre-filtro">
            <span>Mês</span>
            <select value={mes} onChange={e => setMes(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <option key={m} value={m}>{nomeMes(m)}</option>
              ))}
            </select>
          </label>
          <label className="dre-filtro">
            <span>Ano</span>
            <select value={ano} onChange={e => setAno(Number(e.target.value))}>
              {anosDisponiveis.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
          <button
            className="dre-export-btn"
            onClick={exportar}
            disabled={loading || linhasOrdenadas.length === 0}
            title="Exportar a planilha (com os filtros atuais) para Excel"
          >
            <IconDoc size={16} /> Exportar .xlsx
          </button>
        </div>
      </div>

      {/* Detalhe de uma unidade / região */}
      {detalheId ? (
        <div className="dre-unidade-detalhe">
          <div className="pdf-viewer-topbar">
            <button className="back-button" onClick={voltarAoResumo}>← Voltar à planilha</button>
            <div className="pdf-viewer-info">
              <span className="pdf-title">
                <IconBuilding size={18} /> {detalheNome || detalheId}
              </span>
              <span className="pdf-subtitle">
                {ehRegionais
                  ? `Regional ${detalheNome || detalheId} • `
                  : (detalhe && detalhe.regiao ? `Regional ${detalhe.regiao} • ` : '')}
                Somente leitura
              </span>
            </div>
          </div>

          {loadingDetalhe && <p className="loading-text">Carregando {substantivo}...</p>}

          {errorDetalhe && (
            <div className="access-denied-card">
              <span className="access-icon"><IconAlert /></span>
              <h3>Não foi possível carregar</h3>
              <p>{errorDetalhe}</p>
            </div>
          )}

          {detalhe && !errorDetalhe && (
            <>
              <DreIndicadores
                indicadores={detalhe.resultados}
                justificativas={detalhe.justificativas || {}}
                editable={false}
              />
              <DrePlanoAcao planoAcao={detalhe.planoAcao} editable={false} />
            </>
          )}
        </div>
      ) : (
        /* Planilha (regionais ou unidades) */
        <>
          {error && (
            <div className="access-denied-card">
              <span className="access-icon"><IconAlert /></span>
              <h3>Não foi possível carregar</h3>
              <p>{error}</p>
            </div>
          )}

          {loading ? (
            <p className="loading-text">Carregando {substantivoPlural}...</p>
          ) : !error && (
            linhasBrutas.length === 0 ? (
              <p className="empty-message">Nenhuma {substantivo} com dados para este período.</p>
            ) : linhasOrdenadas.length === 0 ? (
              <p className="empty-message">Nenhuma {substantivo} corresponde aos filtros selecionados.</p>
            ) : (
              <>
                <div className="dre-tabela-info">
                  {linhasOrdenadas.length} {linhasOrdenadas.length === 1 ? substantivo : substantivoPlural}
                  {' • '}{nomeMes(mes)}/{ano}
                  {' • '}clique numa coluna para ordenar • clique no nome para o detalhe
                </div>
                <div className="dre-tabela-wrap">
                  <table className="dre-tabela">
                    <thead>
                      <tr>
                        <th
                          rowSpan={2}
                          className="dre-col-unidade dre-th-sort"
                          onClick={() => sortBy('ident')}
                        >
                          {identLabel}{arrow('ident')}
                        </th>
                        {indicadoresMeta.map(ind => (
                          <th key={ind.key} colSpan={3} className="dre-th-group">{ind.label}</th>
                        ))}
                      </tr>
                      <tr>
                        {indicadoresMeta.flatMap(ind => [
                          <th
                            key={`${ind.key}-o`}
                            className="dre-th-sub dre-th-sort dre-th-num"
                            onClick={() => sortBy(`${ind.key}::orcado`)}
                          >
                            Orçado{arrow(`${ind.key}::orcado`)}
                          </th>,
                          <th
                            key={`${ind.key}-r`}
                            className="dre-th-sub dre-th-sort dre-th-num"
                            onClick={() => sortBy(`${ind.key}::realizado`)}
                          >
                            Realizado{arrow(`${ind.key}::realizado`)}
                          </th>,
                          <th
                            key={`${ind.key}-j`}
                            className="dre-th-sub dre-th-sort"
                            onClick={() => sortBy(`${ind.key}::justificativa`)}
                          >
                            Justificativa{arrow(`${ind.key}::justificativa`)}
                          </th>
                        ])}
                      </tr>
                    </thead>
                    <tbody>
                      {linhasOrdenadas.map(row => (
                        <tr key={row.id}>
                          <td className="dre-col-unidade">
                            <button className="dre-unidade-link" onClick={() => abrirDetalhe(row)}>
                              <span className="dre-td-nome">{row.nome}</span>
                              {row.sub && <span className="dre-td-sub">{row.sub}</span>}
                            </button>
                          </td>
                          {row.indicadores.flatMap(ci => {
                            const cor = corRealizado(ci);
                            return [
                              <td key={`${ci.indicador}-o`} className="dre-num">
                                {formatValor(ci.orcado, ci.tipo)}
                              </td>,
                              <td key={`${ci.indicador}-r`} className={`dre-num${cor ? ` dre-var-${cor}` : ''}`}>
                                {formatValor(ci.realizado, ci.tipo)}
                              </td>,
                              <td key={`${ci.indicador}-j`} className="dre-just-cell" title={ci.justificativa || ''}>
                                {ci.justificativa || '—'}
                              </td>
                            ];
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )
          )}
        </>
      )}
    </div>
  );
}

export default DreDiretoria;
